import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const ShopSidebar = ({ 
  categories, 
  selectedCategory, 
  onCategoryChange,
  brands, 
  selectedBrand, 
  onBrandChange,
  priceRange, 
  onPriceChange,
  onClearFilters,
  isOpen,
  onClose,
  onSearch
}) => {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // ✅ NEW: State for collapsible sections
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [brandsExpanded, setBrandsExpanded] = useState(true);

  const toggleCategory = (categoryId) => {
    if (expandedCategories.includes(categoryId)) {
      setExpandedCategories(expandedCategories.filter(id => id !== categoryId));
    } else {
      setExpandedCategories([...expandedCategories, categoryId]);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchTerm);
    }
    if (window.innerWidth <= 768) {
      onClose();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'show' : ''}`} onClick={onClose}></div>
      <div className={`shop-sidebar ${isOpen ? 'open' : ''}`}>
        
        {/* Search Section */}
        <div className="sidebar-search-section">
          <form onSubmit={handleSearch} className="search-form-sidebar">
            <div className="search-wrapper-sidebar">
              <span className="search-icon-sidebar">
                <img 
                  style={{width: '18px', height: '18px'}} 
                  src='https://icons.veryicon.com/png/o/miscellaneous/monochrome-icon-1/search-521.png' 
                  alt="search"
                />
              </span>
              <input
                type="text"
                placeholder={t('filter.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input-sidebar"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="clear-search-sidebar"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <button 
              type="submit" 
              className="search-button-sidebar"
              aria-label="Search"
            >
              {t('header.search')}
            </button>
          </form>
        </div>

        {/* Price Range Section */}
        <div className="filter-section">
          <h3 className="filter-title">{t('filter.price')}</h3>
          <div className="price-inputs">
            <div className="price-input">
              <input
                type="number"
                placeholder={t('filter.min')}
                value={priceRange.min}
                onChange={(e) => onPriceChange({ ...priceRange, min: e.target.value })}
              />
            </div>
            <div className="price-input">
              <input
                type="number"
                placeholder={t('filter.max')}
                value={priceRange.max}
                onChange={(e) => onPriceChange({ ...priceRange, max: e.target.value })}
              />
            </div>
          </div>
          <button className="clear-filters" onClick={onClearFilters}>
            {t('filter.clear')}
          </button>
        </div>

        {/* ✅ UPDATED: Categories Section - Collapsible */}
        <div className="filter-section">
          <div 
            className="filter-section-header"
            onClick={() => setCategoriesExpanded(!categoriesExpanded)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '0.75rem 0',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              marginBottom: '0.5rem'
            }}
          >
            <h3 className="filter-title" style={{ margin: 0 }}>
              {t('filter.category')}
            </h3>
            <span style={{ 
              fontSize: '0.875rem',
              transition: 'transform 0.3s ease',
              transform: categoriesExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}>
              ▼
            </span>
          </div>
          
          {categoriesExpanded && (
            <div className="category-list">
              <div
                className={`category-item ${!selectedCategory ? 'active' : ''}`}
                onClick={() => {
                  onCategoryChange(null);
                  onClose();
                }}
              >
                <span>{t('filter.allProducts')}</span>
              </div>
              
              {categories && categories.length > 0 ? (
                categories.map((category) => (
                  <div key={category.id}>
                    <div
                      className={`category-item ${selectedCategory === category.slug ? 'active' : ''}`}
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span
                        onClick={() => {
                          onCategoryChange(category.slug);
                          onClose();
                        }}
                        style={{ flex: 1, cursor: 'pointer' }}
                      >
                        {category.name}
                      </span>
                      {category.subcategories && category.subcategories.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategory(category.id);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem 0.5rem',
                            fontSize: '0.875rem',
                            color: selectedCategory === category.slug ? 'rgba(255,255,255,0.8)' : 'var(--gray)'
                          }}
                        >
                          {expandedCategories.includes(category.id) ? '▼' : '▶'}
                        </button>
                      )}
                    </div>
                    
                    {/* Subcategories */}
                    {category.subcategories && 
                     category.subcategories.length > 0 && 
                     expandedCategories.includes(category.id) && (
                      <div style={{ paddingLeft: '0.5rem', marginTop: '0.25rem' }}>
                        {category.subcategories.map((subcat) => (
                          <div
                            key={subcat.id}
                            className={`category-item ${selectedCategory === subcat.slug ? 'active' : ''}`}
                            onClick={() => {
                              onCategoryChange(subcat.slug);
                              onClose();
                            }}
                            style={{ 
                              fontSize: '0.9rem',
                              padding: '0.5rem 1rem',
                              cursor: 'pointer'
                            }}
                          >
                            <span>↳ {subcat.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
                  {t('home.loading')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ✅ UPDATED: Brands Section - Collapsible */}
        {brands && brands.length > 0 && (
          <div className="filter-section">
            <div 
              className="filter-section-header"
              onClick={() => setBrandsExpanded(!brandsExpanded)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '0.75rem 0',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                marginBottom: '0.5rem'
              }}
            >
              <h3 className="filter-title" style={{ margin: 0 }}>
                {t('filter.brands') || 'ბრენდები'}
              </h3>
              <span style={{ 
                fontSize: '0.875rem',
                transition: 'transform 0.3s ease',
                transform: brandsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}>
                ▼
              </span>
            </div>
            
            {brandsExpanded && (
              <div className="category-list">
                <div
                  className={`category-item ${!selectedBrand ? 'active' : ''}`}
                  onClick={() => {
                    onBrandChange(null);
                    onClose();
                  }}
                >
                  <span>{t('filter.allBrands') || 'ყველა ბრენდი'}</span>
                </div>
                
                {brands.map((brand) => (
                  <div
                    key={brand.id}
                    className={`category-item ${selectedBrand === brand.slug ? 'active' : ''}`}
                    onClick={() => {
                      onBrandChange(brand.slug);
                      onClose();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer'
                    }}
                  >
                    {brand.logo && (
                      <img 
                        src={`https://kid-o.ge${brand.logo}`}
                        alt={brand.name}
                        style={{
                          width: '24px',
                          height: '24px',
                          objectFit: 'contain',
                          background: 'white',
                          borderRadius: '4px',
                          padding: '2px'
                        }}
                      />
                    )}
                    <span>{brand.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ShopSidebar;